import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import Layout from '../../containers/Layout';
import Navigation from '../../components/Navigation';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useUiStore } from '../../store';
import Tooltip from '../../components/Tooltip';
import { VideoRenderer, ImageRenderer, DocumentRenderer, QuestionRenderer, TextRenderer, AudioRenderer, AssignedSectionRenderer } from './components/PreviewRenderers';
import PreviewContainer from './components/PreviewContainer';
import FooterTimeline from './components/FooterTimeline';
import AddSectionMenu from './components/AddSectionMenu';
import UploadModals from './components/UploadModals';
import ModalsContainer from './components/ModalsContainer';
import { selectAllAssets } from './store/slices/assetsSlice';
import { selectAllUploadStates, selectUploadStatus } from './store/slices/uploadProgressSlice';
import {
  selectItem,
  removeTimelineItem,
} from './store/slices/timelineSlice';
import {
  undo,
  redo,
  selectCanUndo,
  selectCanRedo,
} from './store/slices/undoRedoSlice';
import {
  seek,
  togglePlayPause,
} from './store/slices/playbackSlice';
import {
  setMenuVisible,
  setMenuPosition,
  setPreviewMode,
  setUpdateMeetingTitleModalVisible,
  setSaveSuccessModalVisible,
  setCloseSectionSuccessModalVisible,
  setContributorClosedModalVisible,
  setSaveBeforePublishModalVisible,
  setPermissionManagerResource,
  setPermissionManagerVisible,
  setRequestReviewModalVisible,
  setNotificationManagerVisible,
  setPendingNavigation,
  setLeaveConfirmationVisible,
  setEditingPlaceholderContext,
  setPlaceholderModalVisible,
} from './store/slices/editorUISlice';
import {
  setIsScreenRecordReminderVisible,
} from './store/slices/recordingSlice';
import {
  setMobileFileSelected,
  setMobileUploadCompleted,
  selectIsMobileFileSelected,
  selectIsMobileUploading,
  selectIsMobileUploadComplete,
  resetMobileUpload,
} from './store/slices/mobileUploadSlice';
import { CreateVideoNewPageProvider } from './store/Provider';
import FilledButton from '../../components/Buttons/FilledButton';
import undoIcon from '../../assets/images/create-meeting/undo.svg';
import shareIcon from '../../assets/images/my-meetings/share.svg';
import eyeIcon from '../../assets/images/create-meeting/eye.svg';
import notificationIcon from '../../assets/images/notification.svg';
import usersIcon from '../../assets/images/my-meetings/users.svg';
import createMeetingButtonsClasses from '../../components/CreateMeetingButtons/styles.module.scss';
import UploadMenu from '../../components/UploadMenu';
import FileUploadProgressModal from '../../modals/FileUploadProgressModal';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import useUIHelper from '../../hooks/useUIHelper';
import useScreenWakeLock from '../../hooks/useScreenWakeLock';
import classes from '../CreateOrUpdateMeetingPage/styles.module.scss';
import usePublish from './hooks/usePublish';
import useSaveDraft from './hooks/useSaveDraft';
import useSaveTemplate from './hooks/useSaveTemplate';
import { useRecording } from './hooks/useRecording';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useSectionsPagination } from './hooks/useSectionsPagination';
import { useMeetingData } from './hooks/useMeetingData';
import { useUnsavedChanges, useDirtyHelpers } from './hooks/useUnsavedChanges';
import useNavigationBlocker from './hooks/useNavigationBlocker';
import { createQuestionHandlers } from './lib/handlers/questionHandlers';
import { createTextHandlers } from './lib/handlers/textHandlers';
import { createPlaceholderHandlers } from './lib/handlers/placeholderHandlers';
import { createSectionHandlers } from './lib/handlers/sectionHandlers';
import { createUploadHandlers } from './lib/handlers/uploadHandlers';
import { getVideoDuration } from './lib/assetProcessing';
import {
  getDurationErrorMessage,
  isDurationExceeded,
  isFileSizeExceeded,
  getFileSizeErrorMessage,
} from './lib/uploadLimits';
import UserService from '../../services/UserService';
import SectionService from '../../services/SectionService';
import MeetingService from '../../services/MeetingService';
import useAuthorization from '../../hooks/useAuthorization';
import { useUploadStatus } from './hooks/useUploadStatus';
import { useUiContext } from '../../context/UiContext';
import TemplatesIcon from '../../components/TemplatesIcon';
import PlaceholderPatternOverlay from './components/PlaceholderPatternOverlay';

function CreateVideoNewPage({ editorMode }) {
  const dispatch = useDispatch();
  const plusSignRef = useRef();
  const addSectionHelperShownRef = useRef(false);
  const prevCanShowAddSectionHelperRef = useRef(false);
  const lastAddSectionHelperKeyRef = useRef(null);
  const addSectionContainerRef = useRef();
  const menuRef = useRef();
  const footerTimelineRef = useRef();
  const leaveBlockerRef = useRef(null);
  const uploadModalsRef = useRef();
  const [meetingTitle, setMeetingTitle] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0); // For mobile image uploads
  const { markClean } = useDirtyHelpers();
  const sectionTitleTimersRef = useRef({});
  // Minimal stub to satisfy UploadMenu props for mobile CSS parity
  const createDefaultSectionName = () => 'Video';
  const setSectionTitle = () => {};


  const { id: meetingId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const folderId = searchParams.get('folderId');
  const editorModeFromQuery = searchParams.get('editorMode');
  const isEditMode = !!meetingId;
  const effectiveEditorMode = editorMode || editorModeFromQuery;
  const isTemplateMode = effectiveEditorMode === 'template_creation';
  const isMobile = useCheckMobileScreen();
  const { showHelper, hideHelper } = useUIHelper();

  const { publish, isPublishing, publishProgress } = usePublish();
  const { saveDraft, isSaving, saveProgress, saveProgressInfo } = useSaveDraft();
  const { saveTemplate, isSavingTemplate } = useSaveTemplate();
  const { hasActiveUploads, uploadingCount } = useUploadStatus();
  const { notifyError, notifySuccess } = useUiContext();


  const breadcrumbsOptions = useUiStore((state) => state.breadcrumbsOptions);
  const setBreadcrumbsOptions = useUiStore((state) => state.setBreadcrumbsOptions);
  const queryClient = useQueryClient();

  // Redux state
  const isMenuVisible = useSelector((state) => state.editorUI.isMenuVisible);
  const menuPosition = useSelector((state) => state.editorUI.menuPosition);
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);
  const timelineItems = useSelector((state) => state.timeline.items);
  const selectedItemId = useSelector((state) => state.timeline.selectedItemId);
  const lastAddedItemId = useSelector((state) => state.timeline.lastAddedItemId);
  const allAssets = useSelector(selectAllAssets);

  // Mobile upload state
  const isMobileFileSelected = useSelector(selectIsMobileFileSelected);
  const isMobileUploading = useSelector(selectIsMobileUploading);
  const isMobileUploadComplete = useSelector(selectIsMobileUploadComplete);
  
  // Background upload status (used to track mobile upload progress)
  const backgroundUploadStatus = useSelector(selectUploadStatus);

  const shouldKeepScreenAwake =
    isMobile &&
    (isMobileFileSelected ||
      isMobileUploading ||
      backgroundUploadStatus.hasActiveUploads ||
      uploadProgress > 0);

  const {
    isSupported: isWakeLockSupported,
    lastError: wakeLockError,
    request: requestWakeLock,
  } = useScreenWakeLock(shouldKeepScreenAwake);

  const wakeLockWarningShownRef = useRef(false);

  useEffect(() => {
    if (!shouldKeepScreenAwake || wakeLockWarningShownRef.current) {
      return;
    }

    if (!isWakeLockSupported) {
      wakeLockWarningShownRef.current = true;
      notifyError(
        'Your browser does not support keeping the screen awake. Please disable auto-lock until the upload completes.'
      );
      return;
    }

    if (wakeLockError?.name === 'NotAllowedError') {
      wakeLockWarningShownRef.current = true;
      notifyError(
        'Unable to keep the screen awake. Please keep the app in the foreground and disable auto-lock until the upload completes.'
      );
    }
  }, [shouldKeepScreenAwake, isWakeLockSupported, wakeLockError, notifyError]);

  // Watch for failed uploads and show toast notifications
  // Only show toast for newly failed/completed uploads (not ones that already exist on mount)
  const uploadStates = useSelector(selectAllUploadStates);
  const previousFailedUploadsRef = useRef(new Set());
  const previousCompletedUploadsRef = useRef(new Set());
  const isInitialMountRef = useRef(true);
  
  useEffect(() => {
    // Skip showing toasts on initial mount - mark existing uploads as already shown
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      // Mark all existing failed and completed uploads as already shown
      Object.entries(uploadStates).forEach(([assetId, uploadState]) => {
        if (uploadState?.status === 'failed') {
          previousFailedUploadsRef.current.add(assetId);
        }
        if (uploadState?.status === 'completed') {
          previousCompletedUploadsRef.current.add(assetId);
        }
      });
      return;
    }
    
    // After initial mount, show toast for newly failed uploads and newly completed uploads
    Object.entries(uploadStates).forEach(([assetId, uploadState]) => {
      if (uploadState?.status === 'failed' && !previousFailedUploadsRef.current.has(assetId)) {
        // New failed upload - show toast
        const errorMessage = uploadState.error || 'Upload failed';
        notifyError(errorMessage);
        previousFailedUploadsRef.current.add(assetId);
      } else if (uploadState?.status !== 'failed') {
        // Upload succeeded or is in progress - remove from tracked failures
        previousFailedUploadsRef.current.delete(assetId);
      }
      
      if (uploadState?.status === 'completed' && !previousCompletedUploadsRef.current.has(assetId)) {
        // New completed upload - show success toast with section name
        const asset = allAssets.find((a) => a.id === assetId);
        const sectionName = asset?.name || 'Section';
        notifySuccess(`${sectionName} uploaded successfully`);
        previousCompletedUploadsRef.current.add(assetId);
      } else if (uploadState?.status !== 'completed') {
        // Upload failed or is in progress - remove from tracked completions
        previousCompletedUploadsRef.current.delete(assetId);
      }
    });
  }, [uploadStates, notifyError, notifySuccess, allAssets]);
  const isPreviewMode = useSelector((state) => state.editorUI.isPreviewMode);
  const isUpdateMeetingTitleModalVisible = useSelector((state) => state.editorUI.isUpdateMeetingTitleModalVisible);
  // Editor context from Redux (needed for handlers)
  const editingQuestionContext = useSelector((state) => state.editorUI.editingQuestionContext);
  const editingTextContext = useSelector((state) => state.editorUI.editingTextContext);
  const editingPlaceholderContext = useSelector((state) => state.editorUI.editingPlaceholderContext);
  const focusedSectionTitleId = useSelector((state) => state.editorUI.focusedSectionTitleId);
  const hoveredSectionTitleId = useSelector((state) => state.editorUI.hoveredSectionTitleId);
  const draggedItemId = useSelector((state) => state.editorUI.draggedItemId);
  const dragOverIndex = useSelector((state) => state.editorUI.dragOverIndex);
  const canUndo = useSelector(selectCanUndo);
  const canRedo = useSelector(selectCanRedo);
  // totalDuration is used by SeekingBar component, removed unused assignment here

  // Find current item based on currentTime or selectedItemId
  // When paused at an exact segment boundary, bias slightly backwards to keep current segment visible
  const effectiveTime = isPlaying
    ? currentTime
    : Math.max(0, currentTime - 0.001);

  // Use time-based selection if we've started playback or have a non-zero time
  const currentItem =
    isPlaying || currentTime > 0
      ? (() => {
          // First, check if currentTime exactly matches an item's start_time (common when clicking)
          const exactMatch = timelineItems.find(
            (item) => Math.abs(currentTime - item.start_time) < 0.01
          );
          if (exactMatch) return exactMatch;

          // Check if paused at itemEnd - return that item to keep document renderer correct
          if (!isPlaying) {
            const pausedAtEnd = timelineItems.find((item) => {
              const itemEnd = item.start_time + item.duration;
              return Math.abs(currentTime - itemEnd) < 0.01;
            });
            if (pausedAtEnd) return pausedAtEnd;
          }

          // Otherwise use effectiveTime-based selection
          return (
            timelineItems.find((item) => {
              const itemEnd = item.start_time + item.duration;
              return effectiveTime >= item.start_time && effectiveTime < itemEnd;
            }) ||
            // If past all items, show the last item (at end of playback)
            (timelineItems.length > 0 ? timelineItems[timelineItems.length - 1] : null)
          );
        })()
      : // Otherwise show selected item
        (timelineItems.find((item) => item.id === selectedItemId) ||
          (timelineItems.length > 0 ? timelineItems[0] : null));

  const currentAsset = currentItem
    ? allAssets.find((a) => a.id === currentItem.asset_id)
    : null;

  // Ensure zoom CSS variable is set so menu math/styles match existing editor
  // Note: Don't override the global zoom value as it affects modal sizing
  // The global styles.scss sets --zoom based on screen size (default 0.9)
  useEffect(() => {
    const root = document.documentElement;
    const current = getComputedStyle(root).getPropertyValue('--zoom').trim();
    // Only set if truly not set (shouldn't happen, but safe fallback)
    if (!current || current === '') {
      // Use the same default as global styles.scss
      root.style.setProperty('--zoom', '0.9');
    }
  }, []);

  // Cleanup section title timers on unmount
  useEffect(() => () => {
    Object.values(sectionTitleTimersRef.current).forEach((timer) => {
      if (timer) clearTimeout(timer);
    });
    sectionTitleTimersRef.current = {};
  }, []);

  // Auto-seek to last added section
  useEffect(() => {
    if (lastAddedItemId && timelineItems.length > 0) {
      const lastAddedItem = timelineItems.find((item) => item.id === lastAddedItemId);
      if (lastAddedItem) {
        // Seek to the start of the newly added section
        dispatch(seek(lastAddedItem.start_time));
      }
    }
  }, [lastAddedItemId, timelineItems, dispatch]);

  // Use recording hook
  const recording = useRecording({
    currentTime,
    selectedItemId,
    currentItem,
    dispatch,
  });

  // Destructure recording hook values
  const {
    streamRef,
    videoRef,
    isRecording,
    isCameraEnabled,
    isScreenEnabled,
    isAudioEnabled,
    isTimerRunning,
    timerValue,
    recordingTime,
    isCameraRecordingPaused,
    hasMultipleCameras,
    startCountdownThenStart,
    cancelArming,
    handleRecordSelf,
    handleSwitchCamera,
    handleRecordAudio,
    confirmScreenReminder,
    handlePauseRecording,
    handleResumeRecording,
    handleStopRecording,
  } = recording;

  // Spacebar to play/pause (only when not recording)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle spacebar
      if (e.key !== ' ' && e.code !== 'Space') return;

      // Don't interfere if user is typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        e.target.isContentEditable
      ) {
        return;
      }

      // Don't interfere with recording mode (recording hook handles spacebar for pause/resume)
      if (isRecording || isTimerRunning) {
        return;
      }

      // Prevent default behavior (scrolling)
      e.preventDefault();
      dispatch(togglePlayPause());
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, isRecording, isTimerRunning, togglePlayPause]);

  // Arrow-pagination related shared UI store values
  const sectionsListTranslateX = useUiStore((state) => state.sectionsListTranslateX);

  // Create a temporary state for setCurrentSectionsPage (will be replaced by pagination hook later)
  // This is needed because useMeetingData requires it, but pagination hook provides it
  const [tempCurrentSectionsPage, setTempCurrentSectionsPage] = useState(0);

  const updateAddSectionMenuPosition = useCallback((triggerElement) => {
    if (!triggerElement) {
      return;
    }

    const menuZoom =
      parseFloat(
        getComputedStyle(document.documentElement)
          .getPropertyValue('--zoom')
          .trim()
      ) || 1;
    const triggerCoords = triggerElement.getBoundingClientRect();

    dispatch(
      setMenuPosition({
        left: triggerCoords.left / menuZoom,
        bottom: (window.innerHeight - triggerCoords.bottom) / menuZoom,
      })
    );
  }, [dispatch]);

  // Calculate menu position when it opens
  useEffect(() => {
    if (isMenuVisible && plusSignRef.current) {
      updateAddSectionMenuPosition(plusSignRef.current);
    }
  }, [isMenuVisible, updateAddSectionMenuPosition]);

  // Use meeting data hook
  const { fetchedMeeting, fetchedSections } = useMeetingData({
    meetingId,
    isEditMode,
    setMeetingTitle,
    setCurrentSectionsPage: setTempCurrentSectionsPage, // Use temporary setter
  });

  const { data: user } = useQuery('me', UserService.getMe);
  const { refetch: refetchSections } = useQuery(
    ['sections', meetingId],
    () => SectionService.getSections(meetingId),
    {
      enabled: isEditMode && !!meetingId,
      refetchOnMount: 'always',
      staleTime: 0,
    }
  );

  // Use centralized authorization hook
  const resource = useMemo(() => ({
    userId: fetchedMeeting?.userId,
  }), [fetchedMeeting?.userId]);

  const resourceUser = useMemo(() => {
    // Get role from fetchedMeeting.userRole (set by backend) or MeetingUsers
    const role = fetchedMeeting?.userRole || fetchedMeeting?.MeetingUsers?.[0]?.userRole;
    return role ? { role } : null;
  }, [fetchedMeeting?.userRole, fetchedMeeting?.MeetingUsers]);

  const {
    isSuperAdmin,
    isOrgOwner,
    isFolderOwner,
    isCreator,
    isContributor,
    isContributorClosed,
    isViewer,
    canEditMeeting,
    canCreateMeeting,
    canManageMeetingPermissions,
    meetingUserRole,
    isFolderRoleLoading,
  } = useAuthorization(
    user,
    resource,
    resourceUser,
    'meeting',
    {
      folderId: fetchedMeeting?.folderId || folderId,
      folderUserRole: fetchedMeeting?.folderUserRole || null,
    }
  );

  const { mutateAsync: updateMeeting } = useMutation(
    MeetingService.updateMeeting,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['meeting', meetingId]);
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update video';
        notifyError(errorMessage);
      },
    }
  );

  const shouldShowCloseButton = isContributor; // Show button for all contributors
  const effectiveUserRole = isCreator ? 'creator' : meetingUserRole;
  const canUpdateDraftData =
    isCreator || isOrgOwner || isFolderOwner || isSuperAdmin;

  const hasPlaceholders = timelineItems.some((item) => {
    const asset = allAssets.find((a) => a.id === item.asset_id);
    return asset?.type === 'placeholder';
  });

  let publishTooltipText = 'Share this video with everyone who has access';
  if (hasActiveUploads) {
    publishTooltipText = `Please wait for uploads to finish (${uploadingCount} remaining)`;
  } else if (hasPlaceholders) {
    publishTooltipText = 'Replace all placeholder sections with content before publishing';
  }

  // Redirect viewers and reviewers to viewer page if they somehow access the editor route
  useEffect(() => {
    if (isEditMode && fetchedMeeting && user) {
      // Don't redirect users who can edit (creators, org owners, contributors, folder owners)
      if (canEditMeeting) {
        return;
      }
      
      const hasPendingReview = Array.isArray(fetchedMeeting?.MeetingReviews) && fetchedMeeting.MeetingReviews.length > 0;
      
      // Redirect viewers to viewer page
      // Wait for folder role fetch to complete to avoid bouncing folder owners
      if (isViewer && !isFolderRoleLoading) {
        // Preserve query parameters (e.g., version, reviewId) if present
        const queryString = location.search;
        const redirectUrl = queryString 
          ? `/my-videos/${meetingId}${queryString}`
          : `/my-videos/${meetingId}`;
        navigate(redirectUrl, { replace: true });
        return;
      }
      
      // Redirect reviewers to viewer page with reviewId if they have a pending review
      if (hasPendingReview && fetchedMeeting.MeetingReviews[0]?.id) {
        const reviewId = fetchedMeeting.MeetingReviews[0].id;
        const queryParams = new URLSearchParams(location.search);
        queryParams.set('reviewId', reviewId);
        const redirectUrl = `/my-videos/${meetingId}?${queryParams.toString()}`;
        navigate(redirectUrl, { replace: true });
      }
    }
  }, [isEditMode, fetchedMeeting, isViewer, canEditMeeting, user, meetingId, navigate, location.search]);

  // Calculate filtered timeline items for contributors (hide assigned-section items)
  const visibleTimelineItems = useMemo(() => {
    if (isCreator) {
      return timelineItems; // Creators see everything
    }
    // Contributors: filter out assigned-section items
    return timelineItems.filter((item) => {
      const asset = allAssets.find((a) => a.id === item.asset_id);
      return asset?.type !== 'assigned-section';
    });
  }, [timelineItems, allAssets, isCreator]);

  // Derive completed section IDs from upload states
  const completedSectionIds = useMemo(() => {
    const completedIds = new Set();
    Object.values(uploadStates).forEach((uploadState) => {
      if (uploadState?.status === 'completed' && uploadState.sectionId) {
        completedIds.add(uploadState.sectionId);
      }
    });
    return completedIds;
  }, [uploadStates]);

  const visibleCurrentItemId = currentItem && visibleTimelineItems.some((item) => item.id === currentItem.id)
    ? currentItem.id
    : null;

  // Add Section helper is registered after pagination refs exist.

  useEffect(() => () => hideHelper('add-section-helper'), [hideHelper]);

  // Use sections pagination hook (use filtered items for pagination calculation)
  const pagination = useSectionsPagination({
    timelineItems: visibleTimelineItems,
    currentItemId: visibleCurrentItemId,
  });
  const {
    sectionsListContainerRef,
    sectionsListRef,
    currentSectionsPage,
    setCurrentSectionsPage, // This is the real setter from pagination hook
    sectionsPageCount,
    sectionsCount,
    scrollSections,
  } = pagination;

  useEffect(() => {
    const helperId = 'add-section-helper';
    const canShowHelper =
      !isMobile &&
      !isPreviewMode &&
      !isEditMode &&
      !isContributorClosed &&
      !isUpdateMeetingTitleModalVisible &&
      meetingTitle &&
      timelineItems.length === 0 &&
      !!addSectionContainerRef.current;

    if (!canShowHelper) {
      hideHelper(helperId);
      prevCanShowAddSectionHelperRef.current = false;
      lastAddSectionHelperKeyRef.current = null;
      return;
    }

    const justBecameVisible =
      canShowHelper && !prevCanShowAddSectionHelperRef.current;
    prevCanShowAddSectionHelperRef.current = canShowHelper;

    const helperConfig = {
      id: helperId,
      ref: addSectionContainerRef,
      position: 'top',
      fontSize: 17,
      content: isTemplateMode ? (
        <div style={{ maxWidth: '320px' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#111111', fontSize: '19px' }}>
            Build your template
          </h4>
          <p style={{ margin: 0, color: '#666666', fontSize: '17px', lineHeight: '1.4' }}>
            Click Add Placeholder to define sections that users will fill in when using this template.
          </p>
          <div style={{ fontSize: '14px', color: '#f8585c', fontWeight: 500, marginTop: 10 }}>
            Tip: Give each placeholder a clear title and description so users know what to record.
          </div>
        </div>
      ) : (
        <div style={{ maxWidth: '320px' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#111111', fontSize: '19px' }}>
            Build your video
          </h4>
          <p style={{ margin: 0, color: '#666666', fontSize: '17px', lineHeight: '1.4' }}>
            Click Add Section to record, upload, or insert content into your timeline.
          </p>
          <div style={{ fontSize: '14px', color: '#f8585c', fontWeight: 500, marginTop: 10 }}>
            Tip: Hover between sections to drop new ones exactly where you want them.
          </div>
        </div>
      ),
      backdrop: 'blur',
      arrow: true,
      offset: 12,
    };

    if (justBecameVisible && !addSectionHelperShownRef.current) {
      addSectionHelperShownRef.current = true;
      lastAddSectionHelperKeyRef.current = `${currentSectionsPage}-${sectionsListTranslateX}`;
      showHelper(helperConfig);
      return;
    }

    if (addSectionHelperShownRef.current) {
      const currentKey = `${currentSectionsPage}-${sectionsListTranslateX}`;
      if (currentKey !== lastAddSectionHelperKeyRef.current) {
        lastAddSectionHelperKeyRef.current = currentKey;
        showHelper(helperConfig);
      }
    }
  }, [
    hideHelper,
    isContributorClosed,
    isEditMode,
    isMobile,
    isPreviewMode,
    isUpdateMeetingTitleModalVisible,
    meetingTitle,
    addSectionContainerRef,
    showHelper,
    currentSectionsPage,
    sectionsListTranslateX,
    timelineItems.length,
  ]);

  // Sync temp state with pagination state ONLY when temp changes (from useMeetingData)
  // Don't sync when currentSectionsPage changes (from user interaction) to avoid resetting pagination
  const prevTempPageRef = useRef(tempCurrentSectionsPage);
  useEffect(() => {
    // Only sync if tempCurrentSectionsPage was explicitly changed by useMeetingData
    // (i.e., temp changed, not currentSectionsPage)
    if (prevTempPageRef.current !== tempCurrentSectionsPage) {
      setCurrentSectionsPage(tempCurrentSectionsPage);
      prevTempPageRef.current = tempCurrentSectionsPage;
    }
  }, [tempCurrentSectionsPage, setCurrentSectionsPage]);

  // Show contributor-closed modal when user is contributor-closed
  useEffect(() => {
    if (isContributorClosed) {
      dispatch(setContributorClosedModalVisible(true));
    }
  }, [isContributorClosed, dispatch]);

  useEffect(() => {
    // In edit mode, wait for meeting data to be loaded before building breadcrumbs
    if (isEditMode && (!fetchedMeeting || !fetchedMeeting.id)) {
      return;
    }

    // Check if breadcrumbs already exist (user navigated from My Videos or My Library)
    const existingBreadcrumbs = breadcrumbsOptions;
    
    if (existingBreadcrumbs && existingBreadcrumbs.length > 0) {
      // User navigated from a page - preserve their breadcrumbs and append meeting title
      const lastBreadcrumb = existingBreadcrumbs[existingBreadcrumbs.length - 1];
      const currentTitle = meetingTitle || 'Untitled Video';
      
      // Only update if meeting title changed or if last breadcrumb is not the meeting title
      if (lastBreadcrumb.label !== currentTitle) {
        // Remove the last breadcrumb if it's a placeholder or different meeting
        const breadcrumbsToKeep = lastBreadcrumb.to === '#' 
          ? existingBreadcrumbs.slice(0, -1) 
          : existingBreadcrumbs;
        
    setBreadcrumbsOptions([
          ...breadcrumbsToKeep,
          { label: currentTitle, to: '#' },
        ]);
      }
    } else {
      // No existing breadcrumbs (direct link, refresh, etc.) - build from folder data
      const breadcrumbs = [];
      
      // If editing an existing meeting in a folder, show folder breadcrumbs
      if (isEditMode && fetchedMeeting?.parentFolders && fetchedMeeting.parentFolders.length > 0) {
        breadcrumbs.push({ label: 'My Library', to: '/my-library' });
        // Add parent folders (includes all parents and the meeting's folder)
        fetchedMeeting.parentFolders.forEach((folder) => {
          breadcrumbs.push({
            label: folder.name,
            to: `/my-library/${folder.id}`,
          });
        });
        // Add meeting title as final breadcrumb
        breadcrumbs.push({ label: meetingTitle || 'Untitled Video', to: '#' });
      } else {
        // No folder or creating new meeting - use simple breadcrumbs
        breadcrumbs.push({ label: 'My Videos', to: '/my-videos' });
        breadcrumbs.push({ label: meetingTitle || 'Untitled Video', to: '#' });
      }
      
      setBreadcrumbsOptions(breadcrumbs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingTitle, isEditMode, fetchedMeeting, setBreadcrumbsOptions]);

  // Use unsaved changes hook (tracks beforeunload warning) + flag
  const { hasUnsavedChanges } = useUnsavedChanges();

  useNavigationBlocker(
    hasUnsavedChanges,
    useCallback(
      ({ action, location: nextLocation, proceed }) => {
        leaveBlockerRef.current = { proceed };
        dispatch(
          setPendingNavigation({
            action,
            location: {
              pathname: nextLocation?.pathname,
              search: nextLocation?.search,
              hash: nextLocation?.hash,
            },
          })
        );
        dispatch(setLeaveConfirmationVisible(true));
      },
      [dispatch]
    )
  );

  useEffect(() => {
    if (!hasUnsavedChanges) {
      leaveBlockerRef.current = null;
      dispatch(setPendingNavigation(null));
      dispatch(setLeaveConfirmationVisible(false));
    }
  }, [hasUnsavedChanges, dispatch]);

  const handleConfirmLeave = useCallback(() => {
    if (leaveBlockerRef.current) {
      leaveBlockerRef.current.proceed();
      leaveBlockerRef.current = null;
    }
    dispatch(setPendingNavigation(null));
    dispatch(setLeaveConfirmationVisible(false));
  }, [dispatch]);

  const handleCancelLeave = useCallback(() => {
    leaveBlockerRef.current = null;
    dispatch(setPendingNavigation(null));
    dispatch(setLeaveConfirmationVisible(false));
  }, [dispatch]);




  // Show title modal on first load if no title (only in create mode, desktop only)
  // On mobile, title modal is shown after user selects a file via UploadMenu
  useEffect(() => {
    if (!isMobile && !isEditMode && !meetingTitle && !isUpdateMeetingTitleModalVisible) {
      dispatch(setUpdateMeetingTitleModalVisible(true));
    }
  }, [isMobile, isEditMode, meetingTitle, isUpdateMeetingTitleModalVisible]);

  // Show save success modal after redirect (if navigation state indicates it)
  useEffect(() => {
    if (location.state?.showSaveSuccessModal) {
      dispatch(setSaveSuccessModalVisible(true));
      // Clear the navigation state to prevent showing modal again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, dispatch, navigate, location.pathname]);

  // Close menu on outside click
  useEffect(() => {
    if (!isMenuVisible) {
      return undefined;
    }

    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target)
      ) {
        dispatch(setMenuVisible(false));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuVisible, dispatch]);

  // Playback timer for images/documents - REMOVED - renderers handle their own timers
  // Images play through all items, documents pause at each item end

  // Remove timeline recalculation useEffect - Redux slice handles it automatically

  // Warn user if they try to leave while uploads are in progress
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasActiveUploads) {
        e.preventDefault();
        e.returnValue = `You have ${uploadingCount} upload${uploadingCount > 1 ? 's' : ''} in progress. Are you sure you want to leave?`;
        return e.returnValue;
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasActiveUploads, uploadingCount]);

  // Legacy direct upload helpers removed in favor of parity modals

  const renderPreview = () => {
    if (!currentAsset || !currentItem) {
      return (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#e5e7eb' }}>
            Preview Area
          </div>
          <div style={{ fontSize: 13, marginTop: 6, color: '#9ca3af' }}>
            Add items to timeline to get started
          </div>
        </div>
      );
    }

    if (currentAsset.type === 'video') {
      return <VideoRenderer asset={currentAsset} currentItem={currentItem} />;
    }

    if (currentAsset.type === 'audio') {
      return <AudioRenderer asset={currentAsset} currentItem={currentItem} />;
    }

    const props = {
      asset: currentAsset,
    };
    if (currentAsset.type === 'image') return <ImageRenderer {...props} />;
    if (currentAsset.type === 'document')
      return <DocumentRenderer {...{ ...props, currentItem }} />;
    if (currentAsset.type === 'question')
      return <QuestionRenderer {...{ ...props, currentItem }} />;
    if (currentAsset.type === 'text')
      return <TextRenderer {...{ ...props, currentItem }} />;
    if (currentAsset.type === 'assigned-section')
      return (
        <AssignedSectionRenderer
          {...{ ...props, currentItem }}
          user={user}
          fetchedSections={fetchedSections}
          onAddContent={() => {
            dispatch(setMenuVisible(true));
          }}
        />
      );
    if (currentAsset.type === 'placeholder')
      return (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 640,
              borderRadius: 28,
              background: '#F9FAFB',
              border: '2px dashed #9CA3AF',
              padding: '40px 48px',
              textAlign: 'center',
              boxSizing: 'border-box',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <PlaceholderPatternOverlay
              color="rgba(107, 114, 128, 0.05)"
              fontSize={22}
              rowGap={14}
            />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div
                style={{
                  width: 78,
                  height: 78,
                  borderRadius: '50%',
                  background: 'rgba(248, 88, 92, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}
              >
                <TemplatesIcon size={34} stroke="#F8585C" strokeWidth={1.8} />
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#F8585C',
                  marginBottom: 10,
                }}
              >
                Template Placeholder
              </div>
              <div style={{ fontSize: 28, fontWeight: 600, color: '#111111', lineHeight: '34px' }}>
                {currentAsset.name}
              </div>
              {currentAsset.metadata?.description && (
                <div
                  style={{
                    fontSize: 16,
                    color: '#666666',
                    marginTop: 14,
                    lineHeight: '26px',
                    maxWidth: 460,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                >
                  {currentAsset.metadata.description}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    return <div>Unknown asset type</div>;
  };

  // Create handler modules
  const questionHandlers = createQuestionHandlers({
    dispatch,
    currentTime,
    selectedItemId,
    currentItem,
    isEditMode,
    editingQuestionContext,
  });
  const { handleCreateQuestion, handleOpenEditQuestion, updateQuestionSection } = questionHandlers;

  const textHandlers = createTextHandlers({
    dispatch,
    currentTime,
    selectedItemId,
    currentItem,
    isEditMode,
    editingTextContext,
  });
  const { handleCreateText, handleOpenEditText, updateTextSection } = textHandlers;

  const placeholderHandlers = createPlaceholderHandlers({
    dispatch,
    currentTime,
    selectedItemId,
    currentItem,
    editingPlaceholderContext,
  });
  const {
    handleCreatePlaceholder,
    handleOpenEditPlaceholder,
    updatePlaceholder,
  } = placeholderHandlers;

  const sectionHandlers = createSectionHandlers({
    dispatch,
    sectionTitleTimersRef,
  });
  const { handleSectionTitleChange } = sectionHandlers;

  const uploadHandlers = createUploadHandlers({
    dispatch,
    currentTime,
    selectedItemId,
    currentItem,
    meetingId: meetingId || null,
    notifyError,
  });
  const { handleImageUpload, handleDocumentUpload, handleVideoUpload, handleLibrarySelection } = uploadHandlers;

  // Use drag and drop hook
  const dragAndDrop = useDragAndDrop({
    timelineItems,
    isPreviewMode,
    isEditMode,
    fetchedMeeting,
    fetchedSections,
    user,
    sectionsListRef,
    isCreator,
    isContributor,
  });

  const {
    isItemAllowedToDrag,
    handleDragStart,
    handleDragOver,
    handleSectionDrop,
    handleDragEnd,
  } = dragAndDrop;

  // Ref to hold the file for mobile uploads (used after title is confirmed)
  const pendingMobileFileRef = useRef(null);

  // Mobile file select handler - stores file info and triggers title modal
  const handleMobileFileSelect = useCallback(async (file, fileType) => {
    // eslint-disable-next-line no-console
    console.log('[CreateVideoNewPage] handleMobileFileSelect', {
      name: file?.name,
      size: file?.size,
      type: file?.type,
      fileType,
    });
    if (isFileSizeExceeded(file?.size)) {
      // eslint-disable-next-line no-console
      console.log('[CreateVideoNewPage] file size exceeded');
      notifyError(getFileSizeErrorMessage(file?.name, file?.size));
      dispatch(resetMobileUpload());
      return false;
    }

    if (fileType === 'video') {
      try {
        // eslint-disable-next-line no-console
        console.log('[CreateVideoNewPage] checking video duration');
        const durationSeconds = await getVideoDuration(file);
        // eslint-disable-next-line no-console
        console.log('[CreateVideoNewPage] video duration', durationSeconds);
        if (isDurationExceeded(durationSeconds)) {
          // eslint-disable-next-line no-console
          console.log('[CreateVideoNewPage] duration exceeded');
          notifyError(getDurationErrorMessage(file?.name, durationSeconds));
          dispatch(resetMobileUpload());
          return false;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('[CreateVideoNewPage] failed to read duration', error);
        notifyError('Failed to read video duration. Please try a different file.');
        dispatch(resetMobileUpload());
        return false;
      }
    }
    // Store the file in ref (will be used after title is confirmed)
    pendingMobileFileRef.current = { file, fileType };
    
    // Store file metadata in Redux (for UI display)
    dispatch(setMobileFileSelected({
      fileName: file.name,
      fileType,
    }));
    
    // Title modal will be shown via setIsUpdateMeetingTitleModalVisible in UploadMenu
    return true;
  }, [dispatch, notifyError]);

  // Watch for mobile upload completion via background upload system
  useEffect(() => {
    // When in mobile uploading mode and all background uploads complete
    if (isMobile && isMobileUploading && !backgroundUploadStatus.hasActiveUploads && backgroundUploadStatus.totalCount > 0) {
      // All uploads done - mark as complete which will trigger redirect
      dispatch(setMobileUploadCompleted());
    }
  }, [isMobile, isMobileUploading, backgroundUploadStatus, dispatch]);

  // Watch for mobile image upload completion
  useEffect(() => {
    // When image upload reaches 100%, mark mobile upload as complete
    if (isMobile && !isMobileUploading && uploadProgress === 100) {
      dispatch(setMobileUploadCompleted());
    }
  }, [isMobile, isMobileUploading, uploadProgress, dispatch]);

  // Redirect to My Videos after mobile upload completes
  useEffect(() => {
    if (isMobile && isMobileUploadComplete) {
      // Wait briefly for user to see completion
      const timer = setTimeout(() => {
        // Mark as clean to prevent "Leave without saving?" prompt
        markClean();
        dispatch(resetMobileUpload());
        // Defer navigation so the navigation blocker unmounts after markClean()
        setTimeout(() => {
          navigate('/my-videos?tab=mobile uploads');
        }, 0);
      }, 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isMobile, isMobileUploadComplete, dispatch, navigate, markClean]);

  return (
    <Layout hasUserBlock searchDisabled>
      <div className={`${classes.CreateOrUpdateMeetingPage} ${classes.editorPageRoot}`}>
        <div className={classes.navigationContainer}>
          <Navigation />
        </div>
        <div className={classes.editorWorkspace}>
          <div className={`${classes.container} ${classes.editorBody}`}>
            {/* Mobile-only Upload from my device UI (CSS via onMobile mixin) */}
            <UploadMenu
              createDefaultSectionName={createDefaultSectionName}
              setSectionTitle={setSectionTitle}
              setIsUpdateMeetingTitleModalVisible={(value) => dispatch(setUpdateMeetingTitleModalVisible(value))}
              onFileSelect={handleMobileFileSelect}
              onRequestWakeLock={requestWakeLock}
            />
            {!isMobile && (
            <>
            <div className={classes.innerContainer}>
            <div className={classes.meetingForm}>
              <div className={classes.header}>
                <Breadcrumbs
                  breadcrumbsOptions={breadcrumbsOptions}
                  editableLastItem={
                    canEditMeeting && !isPreviewMode
                      ? {
                          canEdit: true,
                          onUpdate: async (newTitle) => {
                            if (isEditMode && meetingId) {
                              await updateMeeting({ meetingId, title: newTitle });
                            }
                            setMeetingTitle(newTitle);
                          },
                        }
                      : undefined
                  }
                />
                <div className={createMeetingButtonsClasses.CreateMeetingButtons}>
              {/* Undo button */}
              {!isPreviewMode && (
                <Tooltip text="Undo" position="top-right">
                  <button
                    type="button"
                    className={`${createMeetingButtonsClasses.roundButton} ${createMeetingButtonsClasses.undo}`}
                    onClick={() => dispatch(undo())}
                    disabled={!canUndo}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <img src={undoIcon} alt="Undo" />
                  </button>
                </Tooltip>
              )}

              {/* Redo button */}
              {!isPreviewMode && (
                <Tooltip text="Redo" position="top-right">
                  <button
                    type="button"
                    className={`${createMeetingButtonsClasses.roundButton} ${createMeetingButtonsClasses.undo}`}
                    onClick={() => dispatch(redo())}
                    disabled={!canRedo}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', transform: 'scaleX(-1)' }}
                  >
                    <img src={undoIcon} alt="Redo" />
                  </button>
                </Tooltip>
              )}

              {/* Manage access button */}
              {!isPreviewMode && isEditMode && canManageMeetingPermissions && !isTemplateMode && (
                <Tooltip text="Manage access" position="top-right">
                  <button
                    type="button"
                    className={createMeetingButtonsClasses.roundButton}
                    disabled={!fetchedMeeting}
                    onClick={() => {
                      dispatch(
                        setPermissionManagerResource({
                          resourceId: fetchedMeeting?.id,
                          resourceName: fetchedMeeting?.title || meetingTitle,
                        })
                      );
                      dispatch(setPermissionManagerVisible(true));
                    }}
                  >
                    <img src={usersIcon} alt="Manage access" />
                  </button>
                </Tooltip>
              )}

              {/* Request review button */}
              {!isPreviewMode && isEditMode && canManageMeetingPermissions && !isTemplateMode && (
                <Tooltip text="Request review" position="top-right">
                  <button
                    type="button"
                    className={createMeetingButtonsClasses.roundButton}
                    disabled={!fetchedMeeting}
                    onClick={() => {
                      dispatch(setRequestReviewModalVisible(true));
                    }}
                  >
                    <img src={shareIcon} alt="Request review" />
                  </button>
                </Tooltip>
              )}

              {/* Reminders button */}
              {!isPreviewMode && isEditMode && canManageMeetingPermissions && !isTemplateMode && (
                <Tooltip text="Reminders" position="top-right">
                  <button
                    type="button"
                    className={createMeetingButtonsClasses.roundButton}
                    disabled={!fetchedMeeting}
                    onClick={() => dispatch(setNotificationManagerVisible(true))}
                  >
                    <img
                      src={notificationIcon}
                      alt="Reminders"
                      style={{
                        width: 70,
                        height: 70,
                        display: 'block',
                        margin: '-9px 0 0 -10px',
                        objectFit: 'contain',
                        opacity: 0.5,
                      }}
                    />
                  </button>
                </Tooltip>
              )}

              {/* Save and Publish buttons */}
              {isPreviewMode ? (
                <div>
                  <FilledButton
                    width={180}
                    style={{ height: 50 }}
                    onClick={() => dispatch(setPreviewMode(false))}
                  >
                    Edit
                  </FilledButton>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '10px' }}>
                  {isTemplateMode ? (
                    <Tooltip text="Save this template" position="top-right">
                      <div>
                        <FilledButton
                          isDisabled={timelineItems.length === 0 || isSavingTemplate}
                          width={250}
                          style={{ height: 50, whiteSpace: 'nowrap' }}
                          onClick={() => {
                            if (!meetingTitle) {
                              dispatch(setUpdateMeetingTitleModalVisible(true));
                              return;
                            }
                            saveTemplate({ title: meetingTitle, meetingId });
                          }}
                        >
                          {isSavingTemplate ? 'Saving...' : 'Save Template'}
                        </FilledButton>
                      </div>
                    </Tooltip>
                  ) : (
                    <>
                  {/* Preview button - same visibility/disable rules as Save Draft */}
                  {((!isEditMode && canCreateMeeting) || (isEditMode && canEditMeeting)) && (
                    <Tooltip
                      text="Preview last saved state of the video"
                      position="top-right"
                      width={210}
                    >
                      <button
                        type="button"
                        className={createMeetingButtonsClasses.roundButton}
                        disabled={timelineItems.length === 0 || isSaving || isPublishing || isContributorClosed || hasActiveUploads}
                        onClick={() => {
                          if (!meetingId) return;
                          window.open(`/my-videos/${meetingId}`, '_blank', 'noopener');
                        }}
                      >
                        <img src={eyeIcon} alt="Preview" />
                      </button>
                    </Tooltip>
                  )}

                  {/* Save Draft button - use canCreateMeeting for new meetings, canEditMeeting for existing meetings */}
                  {((!isEditMode && canCreateMeeting) || (isEditMode && canEditMeeting)) && (() => {
                    let saveButtonText = 'Save draft';
                    if (isSaving) {
                      saveButtonText = 'Saving...';
                    } else if (isContributor) {
                      saveButtonText = 'Save changes';
                    }

                    let tooltipText;
                    if (hasActiveUploads) {
                      tooltipText = `Please wait for uploads to finish (${uploadingCount} remaining)`;
                    } else if (isContributor) {
                      tooltipText = "Save changes without publishing";
                    } else {
                      tooltipText = "Save draft without publishing";
                    }

                    return (
                      <Tooltip
                        text={tooltipText}
                        position="top-right"
                      >
                        <div>
                          <FilledButton
                            isDisabled={timelineItems.length === 0 || isSaving || isPublishing || isContributorClosed || hasActiveUploads}
                            width={isContributor ? 220 : 180}
                            style={{ height: 50 }}
                            onClick={async () => {
                              if (timelineItems.length === 0) {
                                return;
                              }
                              if (!meetingTitle) {
                                dispatch(setUpdateMeetingTitleModalVisible(true));
                                return;
                              }
                              
                              try {
                                const pendingSaves = footerTimelineRef.current?.getPendingSaveItemIds() || new Set();
                                await saveDraft({
                                  title: meetingTitle,
                                  folderId,
                                  meetingId,
                                  assets: allAssets,
                                  timelineItems,
                                  userRole: effectiveUserRole,
                                  userId: user?.id,
                                  canUpdateDraftData,
                                  pendingSaveItemIds: pendingSaves,
                                });
                                // Clear pending saves after successful draft save
                                // (refetch rebuilds timeline with new item IDs, so old IDs become stale)
                                if (pendingSaves.size > 0) {
                                  footerTimelineRef.current?.clearPendingSaveItemIds();
                                }
                              } catch (error) {
                                // Error is already handled in useSaveDraft hook
                              }
                            }}
                          >
                            {saveButtonText}
                          </FilledButton>
                        </div>
                      </Tooltip>
                    );
                  })()}

                  {/* Publish button - use canCreateMeeting for new meetings, isCreator for existing meetings */}
                  {((!isEditMode && canCreateMeeting) || (isEditMode && isCreator)) && (
                  <Tooltip
                    text={publishTooltipText}
                    position="top-left"
                    width={320}
                  >
                    <div>
                      <FilledButton
                        data-testid="publish-button"
                        isDisabled={timelineItems.length === 0 || isPublishing || isSaving || hasActiveUploads || hasPlaceholders}
                        width={180}
                        style={{ height: 50 }}
                        onClick={() => {
                          if (timelineItems.length === 0) {
                            return;
                          }
                          if (!meetingTitle) {
                            dispatch(setUpdateMeetingTitleModalVisible(true));
                            return;
                          }
                          
                          // Always show save before publish confirmation modal
                          dispatch(setSaveBeforePublishModalVisible(true));
                        }}
                      >
                        Publish
                      </FilledButton>
                    </div>
                  </Tooltip>
                  )}

                  {/* Notify Owner button - visible for all contributors */}
                  {shouldShowCloseButton && (
                    <Tooltip
                      text="Notify the owner about your addition"
                      position="top-right"
                      width={200}
                    >
                       <div>
                         <FilledButton
                           isDisabled={isSaving || isPublishing}
                           width={220}
                           style={{ height: 50 }}
                          onClick={async () => {
                            try {
                              // Check if there are unsaved sections (contributor-created sections without sectionId)
                              const hasUnsavedSections = timelineItems.some((item) => {
                                const asset = allAssets.find((a) => a.id === item.asset_id);
                                // Unsaved sections are those with source_type: 'upload' and no sectionId
                                return asset?.source_type === 'upload' 
                                  && asset?.type !== 'assigned-section' 
                                  && !asset?.metadata?.sectionId;
                              });

                              // If there are unsaved sections, save them first
                              if (hasUnsavedSections) {
                                if (!meetingTitle) {
                                  dispatch(setUpdateMeetingTitleModalVisible(true));
                                  return;
                                }
                                
                                // Save draft first (skip success modal during notify flow)
                                const pendingSaves = footerTimelineRef.current?.getPendingSaveItemIds() || new Set();
                                await saveDraft({
                                  title: meetingTitle,
                                  folderId,
                                  meetingId,
                                  assets: allAssets,
                                  timelineItems,
                                  userRole: effectiveUserRole,
                                  userId: user?.id,
                                  canUpdateDraftData,
                                  skipSuccessModal: true, // Skip showing "Draft saved" modal
                                  pendingSaveItemIds: pendingSaves,
                                });
                                // Clear pending saves after successful draft save
                                if (pendingSaves.size > 0) {
                                  footerTimelineRef.current?.clearPendingSaveItemIds();
                                }
                              }

                              // Now notify the owner
                              await MeetingService.notifyOwner(meetingId);
                              // Show success modal
                              dispatch(setCloseSectionSuccessModalVisible(true));
                            } catch (error) {
                              console.error('Error notifying owner:', error);
                              // Error handling can be added here if needed
                            }
                          }}
                         >
                           Notify Owner
                         </FilledButton>
                       </div>
                    </Tooltip>
                  )}
                    </>
                  )}
                </div>
              )}
                </div>
              </div>
              {/* Removed extra meeting title header duplicate for parity with legacy layout */}
            </div>
            </div>
            <div className={classes.mediaContainerContainer}>
              <PreviewContainer
                videoRef={videoRef}
                isAudioEnabled={isAudioEnabled}
                isRecording={isRecording}
                isTimerRunning={isTimerRunning}
                isCameraEnabled={isCameraEnabled}
                isScreenEnabled={isScreenEnabled}
                streamRef={streamRef}
                timerValue={timerValue}
                recordingTime={recordingTime}
                isCameraRecordingPaused={isCameraRecordingPaused}
                hasMultipleCameras={hasMultipleCameras}
                renderPreview={renderPreview}
                startCountdownThenStart={startCountdownThenStart}
                handlePauseRecording={handlePauseRecording}
                handleResumeRecording={handleResumeRecording}
                handleStopRecording={handleStopRecording}
                cancelArming={cancelArming}
                handleSwitchCamera={handleSwitchCamera}
                currentItem={currentItem}
                currentAsset={currentAsset}
              />
            </div>
            </>
            )}
          </div>

          {/* Footer strip container to match existing layout */}
          {!isMobile && (
          <div className={classes.desktopFooterWrapper}>
            <FooterTimeline
              ref={footerTimelineRef}
              sectionsListContainerRef={sectionsListContainerRef}
              sectionsListRef={sectionsListRef}
              sectionsCount={sectionsCount}
              currentSectionsPage={currentSectionsPage}
              sectionsPageCount={sectionsPageCount}
              sectionsListTranslateX={sectionsListTranslateX}
              scrollSections={scrollSections}
              handleDragOver={handleDragOver}
              handleSectionDrop={handleSectionDrop}
              handleDragEnd={handleDragEnd}
              timelineItems={visibleTimelineItems}
              allAssets={allAssets}
              isPreviewMode={isPreviewMode}
              plusSignRef={plusSignRef}
              addSectionContainerRef={addSectionContainerRef}
              isMenuVisible={isMenuVisible}
              setMenuVisible={(value) => dispatch(setMenuVisible(value))}
              selectItem={(id) => dispatch(selectItem(id))}
              currentItem={currentItem}
              draggedItemId={draggedItemId}
              dragOverIndex={dragOverIndex}
              focusedSectionTitleId={focusedSectionTitleId}
              hoveredSectionTitleId={hoveredSectionTitleId}
              isEditMode={isEditMode}
              handleSectionTitleChange={handleSectionTitleChange}
              handleOpenEditQuestion={handleOpenEditQuestion}
              handleOpenEditText={handleOpenEditText}
              handleOpenEditPlaceholder={handleOpenEditPlaceholder}
              handleDragStart={handleDragStart}
              isItemAllowedToDrag={isItemAllowedToDrag}
              removeTimelineItem={(id) => dispatch({ ...removeTimelineItem(id), meta: { undoable: true } })}
              user={user}
              fetchedSections={fetchedSections}
              isCreator={isCreator}
              completedSectionIds={completedSectionIds}
              isTemplateMode={isTemplateMode}
              onAddPlaceholder={() => {
                dispatch(setEditingPlaceholderContext(null));
                dispatch(setPlaceholderModalVisible(true));
              }}
              onPlaceholderAddContent={(placeholderItem, triggerElement) => {
                if (triggerElement) {
                  plusSignRef.current = triggerElement;
                  updateAddSectionMenuPosition(triggerElement);
                }
                dispatch(selectItem(placeholderItem.id));
                dispatch(setMenuVisible(true));
              }}
            />
          </div>
          )}
        </div>
      </div>
      <AddSectionMenu
              isCreator={isCreator}
              isContributorClosed={isContributorClosed}
        isMobile={isMobile}
        isMenuVisible={isMenuVisible}
        menuPosition={menuPosition}
        menuRef={menuRef}
        isEditMode={isEditMode}
        meetingTitle={meetingTitle}
        handleRecordSelf={handleRecordSelf}
        handleRecordAudio={handleRecordAudio}
        setIsScreenRecordReminderVisible={(value) => dispatch(setIsScreenRecordReminderVisible(value))}
        setIsImportFileChoiceVisible={(value) => uploadModalsRef.current?.setIsImportFileChoiceVisible(value)}
        setIsUploadImagesModalVisible={(value) => uploadModalsRef.current?.setIsUploadImagesModalVisible(value)}
        setIsUploadDocumentsModalVisible={(value) => uploadModalsRef.current?.setIsUploadDocumentsModalVisible(value)}
      />
      <UploadModals
        ref={uploadModalsRef}
        handleImageUpload={handleImageUpload}
        handleDocumentUpload={handleDocumentUpload}
        handleVideoUpload={handleVideoUpload}
        handleLibrarySelection={handleLibrarySelection}
      />
      <ModalsContainer
        meetingId={meetingId}
        isEditMode={isEditMode}
        meetingTitle={meetingTitle}
        setMeetingTitle={setMeetingTitle}
        folderId={folderId}
        fetchedMeeting={fetchedMeeting}
        fetchedSections={fetchedSections}
        timelineItems={timelineItems}
        allAssets={allAssets}
        footerTimelineRef={footerTimelineRef}
        publish={publish}
        isPublishing={isPublishing}
        publishProgress={publishProgress}
        refetchSections={refetchSections}
        saveDraft={saveDraft}
        isSaving={isSaving}
        saveProgress={saveProgress}
        saveProgressInfo={saveProgressInfo}
        user={user}
        canUpdateDraftData={canUpdateDraftData}
        handleCreateText={handleCreateText}
        handleCreateQuestion={handleCreateQuestion}
        handleCreatePlaceholder={handleCreatePlaceholder}
        updateTextSection={updateTextSection}
        updateQuestionSection={updateQuestionSection}
        updatePlaceholder={updatePlaceholder}
        editingQuestionContext={editingQuestionContext}
        confirmScreenReminder={confirmScreenReminder}
        onConfirmLeave={handleConfirmLeave}
        onCancelLeave={handleCancelLeave}
        isMobile={isMobile}
        isMobileFileSelected={isMobileFileSelected}
        pendingMobileFileRef={pendingMobileFileRef}
        handleVideoUpload={handleVideoUpload}
        handleImageUpload={handleImageUpload}
        setUploadProgress={setUploadProgress}
        isTemplateMode={isTemplateMode}
      />
      {/* Mobile upload progress modal */}
      {isMobile && (isMobileUploading || uploadProgress > 0) && (
        <FileUploadProgressModal
          show={isMobileUploading || uploadProgress > 0}
          uploadProgress={(() => {
            // For videos: compute progress from background upload states
            // For images: use local upload progress
            if (uploadProgress > 0) {
              return uploadProgress; // Image upload progress
            }
            const uploads = Object.values(uploadStates);
            if (uploads.length === 0) return 0;
            const totalProgress = uploads.reduce((sum, u) => sum + (u?.progress || 0), 0);
            return Math.round(totalProgress / uploads.length);
          })()}
          isSectionBeingCreated={backgroundUploadStatus.hasActiveUploads || uploadProgress > 0}
          handleClose={() => {
            markClean();
            dispatch(resetMobileUpload());
            setUploadProgress(0); // Reset local progress
            // Defer navigation so the navigation blocker unmounts after markClean()
            setTimeout(() => {
              navigate('/my-videos?tab=mobile uploads');
            }, 0);
          }}
          setUploadProgress={setUploadProgress}
        />
      )}
    </Layout>
  );
}

export default function CreateVideoNewPageWithProvider({ editorMode }) {
  return (
    <CreateVideoNewPageProvider>
      <CreateVideoNewPage editorMode={editorMode} />
    </CreateVideoNewPageProvider>
  );
}
